(async () => {
  const { StandardEvents } = await import('@shopify/events');
  if (customElements.get('vac-card-buy')) return;
  customElements.define('vac-card-buy', class extends HTMLElement {
    static active = null;
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.busy = false;
      this.removeAttribute('data-busy');
      const { signal } = this.controller;
      this.formComponent = this.querySelector('product-form-component');
      this.form = this.querySelector('form');
      this.toggleButton = this.querySelector('[data-vac-toggle]');
      this.addButton = this.querySelector('[data-vac-add]');
      this.sizes = [...this.querySelectorAll('[data-vac-size]')];
      this.variantInput = this.querySelector('input[name="id"]');
      this.status = this.querySelector('[data-vac-status]');
      this.error = this.querySelector('[ref="addToCartTextError"]');
      if (!this.form || !this.toggleButton || !this.variantInput) return;
      this.close(false);
      customElements.whenDefined('product-form-component').then(() => {
        if (!signal.aborted) this.toggleButton.disabled = false;
      });
      this.addEventListener('click', event => {
        event.stopPropagation();
        const button = event.target.closest('button');
        if (!button || button.disabled || this.busy) return;
        if (button === this.toggleButton) {
          if (this.hasAttribute('data-open')) this.close(true);
          else this.open();
        } else if (button.matches('[data-vac-size]')) this.select(button);
      }, { signal });
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.hasAttribute('data-open')) {
          event.preventDefault();event.stopPropagation();this.close(true);
        }
        if (!event.target.matches('[data-vac-size]')) return;
        const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
        if (!keys.includes(event.key)) return;
        event.preventDefault();
        const available = this.sizes.filter(button => !button.disabled);
        const index = available.indexOf(event.target);
        let next = index + (['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 1);
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = available.length - 1;
        available[(next + available.length) % available.length]?.focus();
      }, { signal });
      this.addEventListener('submit', event => {
        const selected = this.sizes.find(button => button.getAttribute('aria-pressed') === 'true');
        if (this.busy || !this.hasAttribute('data-open') || !selected || selected.disabled || this.variantInput.value !== selected.dataset.vacSize) {
          event.preventDefault();event.stopImmediatePropagation();return;
        }
        this.error.classList.add('hidden');
      }, { capture: true, signal });
      this.addEventListener(StandardEvents.cartLinesUpdate, event => {
        if (event.target !== this.formComponent || event.action !== 'add' || !event.promise) return;
        this.setBusy(true);
        this.toggleButton.focus({ preventScroll: true });
        event.promise.then(({ detail }) => {
          if (signal.aborted) return;
          this.setBusy(false);
          if (detail?.didError) return;
          this.close(false);
          this.setAttribute('data-added', '');
          this.status.textContent = this.dataset.addedLabel;
          this.toggleButton.setAttribute('aria-label', this.dataset.addedLabel);
          this.feedbackTimer = setTimeout(() => {
            this.removeAttribute('data-added');
            this.toggleButton.setAttribute('aria-label', this.dataset.openLabel);
          }, 1600);
        }).catch(() => {
          if (signal.aborted) return;
          this.setBusy(false);
          this.error.textContent = this.dataset.errorLabel;
          this.error.classList.remove('hidden');
        });
      }, { signal });
      document.addEventListener('click', event => {
        if (this.hasAttribute('data-open') && !this.contains(event.target)) this.close(false);
      }, { signal });
      document.addEventListener('focusin', event => {
        // Pointer focus precedes click. Closing an in-flow selector here would
        // move the next card before pointerup and swallow its click. Let click
        // (or the next card's open()) close it; keep immediate keyboard closing.
        const target = event.target;
        if (target instanceof HTMLElement && target.matches(':focus-visible') &&
          this.hasAttribute('data-open') && !this.contains(target)) this.close(false);
      }, { signal });
      window.addEventListener('pageshow', () => { if (!this.busy) this.close(false); }, { signal });
    }
    disconnectedCallback() {
      this.controller?.abort();clearTimeout(this.feedbackTimer);
      if (this.constructor.active === this) this.constructor.active = null;
    }
    open() {
      const previous = this.constructor.active;
      if (previous && previous !== this && !previous.close(false)) return;
      this.close(false);this.constructor.active = this;
      this.setAttribute('data-open', '');
      this.toggleButton.setAttribute('aria-expanded', 'true');
      this.toggleButton.setAttribute('aria-label', this.dataset.closeLabel);
      this.toggleButton.title = this.dataset.closeLabel;
      this.sizes.forEach(button => { button.hidden = false; });
      if (this.toggleButton.matches(':focus-visible')) this.sizes.find(button => !button.disabled)?.focus();
    }
    close(restoreFocus) {
      if (this.busy) return false;
      clearTimeout(this.feedbackTimer);
      this.removeAttribute('data-open');this.removeAttribute('data-added');
      this.toggleButton.setAttribute('aria-expanded', 'false');
      this.toggleButton.setAttribute('aria-label', this.dataset.openLabel);
      this.toggleButton.title = this.dataset.openLabel;
      this.sizes.forEach(button => { button.hidden = true;button.setAttribute('aria-pressed', 'false'); });
      this.variantInput.value = '';this.variantInput.disabled = true;
      this.addButton.hidden = true;this.addButton.disabled = true;
      this.error.classList.add('hidden');this.status.textContent = '';
      if (this.constructor.active === this) this.constructor.active = null;
      if (restoreFocus) this.toggleButton.focus({ preventScroll: true });
      return true;
    }
    select(button) {
      if (button.dataset.unavailable === 'true' || !this.hasAttribute('data-open') || this.busy) return;
      this.sizes.forEach(size => size.setAttribute('aria-pressed', String(size === button)));
      this.variantInput.value = button.dataset.vacSize;this.variantInput.disabled = false;
      this.addButton.disabled = false;this.addButton.hidden = true;
      this.addButton.setAttribute('aria-label', `${this.dataset.addLabel} · ${button.dataset.label}`);
      this.addButton.title = `${this.dataset.addLabel} · ${button.dataset.label}`;
      this.error.classList.add('hidden');
      this.form.requestSubmit(this.addButton);
    }
    setBusy(busy) {
      this.busy = busy;this.toggleAttribute('data-busy', busy);
      this.form.setAttribute('aria-busy', String(busy));
      this.sizes.forEach(button => { button.disabled = busy || button.dataset.unavailable === 'true'; });
      this.addButton.disabled = busy || !this.variantInput.value;
      this.toggleButton.setAttribute('aria-disabled', String(busy));
    }
  });
})();
